/* Shared Lens presentation formatting contract.
   Mirrors Ledger's Settings -> Formatting defaults without making Lens a
   writer of Ledger facts. Money helpers stay currency-neutral unless a
   Book/reporting currency or caller-supplied currency is available. */

export const PRESENTATION_SETTINGS_STORAGE_KEY = 'cleic-ledger-presentation-settings-v1';

export const DATE_FORMATS = Object.freeze(['yyyy-MM-dd', 'yyyy-MMM-dd', 'MMM d, yyyy']);
export const TIME_FORMATS = Object.freeze(['HH:mm', 'HH:mm:ss', 'h:mm a']);
export const NUMBER_FORMATS = Object.freeze(['#,###.00', '#,###', '#,###.0000']);
export const ROUNDING_PLACEMENTS = Object.freeze(['none', 'K', 'M', 'B', 'T', 'Q']);
export const ROUNDING_MODES = Object.freeze(['nearest', 'down', 'up']);

export const DEFAULT_PRESENTATION_SETTINGS = Object.freeze({
  dateFormat: 'yyyy-MM-dd',
  numberFormat: '#,###.00',
  timeFormat: 'HH:mm',
  roundingPlacement: 'none',
  roundingMode: 'nearest',
  defaultCurrency: null,
});

const NUMBER_LOCALE = 'en-001';
const MONTH_ABBREV = Object.freeze(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']);
const CURRENCY_SYMBOLS = Object.freeze({
  PHP: '₱',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'CA$',
  AUD: 'A$',
  SGD: 'S$',
  HKD: 'HK$',
});
const CURRENCY_LOCALES = Object.freeze({
  PHP: 'en-PH',
});
const ROUNDING_FACTORS = Object.freeze({
  none: 1,
  K: 1_000,
  M: 1_000_000,
  B: 1_000_000_000,
  T: 1_000_000_000_000,
  Q: 1_000_000_000_000_000,
});

let currentSettings = normalizePresentationSettings(loadStoredPresentationSettings());

function includes(options, value) {
  return typeof value === 'string' && options.includes(value);
}

function normalizeDateFormat(value) {
  if (value === 'YYYY-MM-DD') return 'yyyy-MM-dd';
  return includes(DATE_FORMATS, value) ? value : DEFAULT_PRESENTATION_SETTINGS.dateFormat;
}

function normalizeNumberFormat(value) {
  if (value === '1,000.00') return '#,###.00';
  if (value === '1,000') return '#,###';
  return includes(NUMBER_FORMATS, value) ? value : DEFAULT_PRESENTATION_SETTINGS.numberFormat;
}

export function normalizePresentationSettings(value) {
  const candidate = value && typeof value === 'object' ? value : {};
  return {
    dateFormat: normalizeDateFormat(candidate.dateFormat),
    numberFormat: normalizeNumberFormat(candidate.numberFormat),
    timeFormat: includes(TIME_FORMATS, candidate.timeFormat)
      ? candidate.timeFormat
      : DEFAULT_PRESENTATION_SETTINGS.timeFormat,
    roundingPlacement: includes(ROUNDING_PLACEMENTS, candidate.roundingPlacement)
      ? candidate.roundingPlacement
      : DEFAULT_PRESENTATION_SETTINGS.roundingPlacement,
    roundingMode: includes(ROUNDING_MODES, candidate.roundingMode)
      ? candidate.roundingMode
      : DEFAULT_PRESENTATION_SETTINGS.roundingMode,
    defaultCurrency: normalizeCurrencyCode(candidate.defaultCurrency),
  };
}

function normalizeCurrencyCode(value) {
  return typeof value === 'string' && value.trim()
    ? value.trim().toUpperCase()
    : null;
}

export function loadStoredPresentationSettings() {
  try {
    if (typeof window === 'undefined') return DEFAULT_PRESENTATION_SETTINGS;
    const raw = window.localStorage.getItem(PRESENTATION_SETTINGS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : DEFAULT_PRESENTATION_SETTINGS;
  } catch {
    return DEFAULT_PRESENTATION_SETTINGS;
  }
}

export function setLensPresentationSettings(settings, bookSettings) {
  const bookCurrency = bookSettings?.reportingCurrency || bookSettings?.defaultCurrency;
  currentSettings = normalizePresentationSettings({
    ...currentSettings,
    ...settings,
    defaultCurrency: bookCurrency || settings?.defaultCurrency || null,
  });
  return currentSettings;
}

export function getLensPresentationSettings() {
  return currentSettings;
}

export function numberFormatDecimals(format = currentSettings.numberFormat) {
  if (format === '#,###') return 0;
  if (format === '#,###.0000') return 4;
  return 2;
}

function roundScaled(value, mode) {
  if (mode === 'down') return value < 0 ? Math.ceil(value) : Math.floor(value);
  if (mode === 'up') return value < 0 ? Math.floor(value) : Math.ceil(value);
  return Math.round(value);
}

function roundToDecimals(value, decimals, mode) {
  const factor = 10 ** decimals;
  return roundScaled(value * factor, mode) / factor;
}

function scaledNumber(value, settings = currentSettings) {
  const factor = ROUNDING_FACTORS[settings.roundingPlacement] || 1;
  if (settings.roundingPlacement === 'none' || factor === 1) {
    return { value, suffix: '' };
  }
  return {
    value: roundScaled(value / factor, settings.roundingMode),
    suffix: settings.roundingPlacement,
  };
}

export function formatPlainNumber(value, { decimals, settings = currentSettings, scale = true } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const resolved = scale ? scaledNumber(numeric, settings) : { value: numeric, suffix: '' };
  const digits = decimals ?? numberFormatDecimals(settings.numberFormat);
  const body = new Intl.NumberFormat(NUMBER_LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(resolved.value);
  return `${body}${resolved.suffix}`;
}

function currencyLocale(code) {
  return CURRENCY_LOCALES[code] || NUMBER_LOCALE;
}

export function currencySymbol(currencyCode = currentSettings.defaultCurrency) {
  const code = normalizeCurrencyCode(currencyCode);
  if (!code) return '';
  return CURRENCY_SYMBOLS[code] || `${code} `;
}

export function formatMoney(value, { currencyCode, decimals, settings = currentSettings, scale = true } = {}) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const sign = numeric < 0 ? '−' : '';
  const code = normalizeCurrencyCode(currencyCode || settings.defaultCurrency);
  const resolved = scale ? scaledNumber(Math.abs(numeric), settings) : { value: Math.abs(numeric), suffix: '' };
  const digits = decimals ?? numberFormatDecimals(settings.numberFormat);
  if (!code) {
    return `${sign}${formatPlainNumber(resolved.value, { decimals: digits, settings, scale: false })}${resolved.suffix}`;
  }
  try {
    const body = new Intl.NumberFormat(currencyLocale(code), {
      style: 'currency',
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(resolved.value);
    return `${sign}${body}${resolved.suffix}`;
  } catch {
    const symbol = currencySymbol(code);
    return `${sign}${symbol}${formatPlainNumber(resolved.value, { decimals: digits, settings, scale: false })}${resolved.suffix}`;
  }
}

export function formatMoneyShort(value, { currencyCode, settings = currentSettings } = {}) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const sign = numeric < 0 ? '−' : '';
  const abs = Math.abs(numeric);
  const code = normalizeCurrencyCode(currencyCode || settings.defaultCurrency);
  const symbol = currencySymbol(code);
  const placement = [...ROUNDING_PLACEMENTS]
    .filter((p) => p !== 'none')
    .reverse()
    .find((p) => abs >= ROUNDING_FACTORS[p]);
  if (!placement) {
    return `${sign}${symbol}${formatPlainNumber(abs, { decimals: 0, settings, scale: false })}`;
  }
  const decimals = placement === 'K' ? 1 : 2;
  const rounded = roundToDecimals(abs / ROUNDING_FACTORS[placement], decimals, settings.roundingMode);
  return `${sign}${symbol}${formatPlainNumber(rounded, { decimals, settings, scale: false })}${placement}`;
}

function dateParts(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { year: value.getFullYear(), monthIndex: value.getMonth(), day: value.getDate() };
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) return null;
  return { year: Number(match[1]), monthIndex: Number(match[2]) - 1, day: Number(match[3]) };
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

export function formatDateOnly(value, settings = currentSettings) {
  const parts = dateParts(value);
  if (!parts) return '—';
  const yyyy = String(parts.year);
  const mm = pad2(parts.monthIndex + 1);
  const dd = pad2(parts.day);
  if (settings.dateFormat === 'yyyy-MMM-dd') return `${yyyy}-${MONTH_ABBREV[parts.monthIndex] || '???'}-${dd}`;
  if (settings.dateFormat === 'MMM d, yyyy') return `${MONTH_ABBREV[parts.monthIndex] || '???'} ${parts.day}, ${yyyy}`;
  return `${yyyy}-${mm}-${dd}`;
}

export function todayISO() {
  return formatDateOnly(new Date(), { ...currentSettings, dateFormat: 'yyyy-MM-dd' });
}
